import React from 'react'
import { clone, includes, debounce } from 'lodash'
import { Redirect } from 'react-router-dom'
import { Card, CardActions, CardText, CardTitle } from 'material-ui-legacy/Card'
import DatePicker from 'material-ui-legacy/DatePicker'
import Paper from 'material-ui-legacy/Paper'
import TextField from 'material-ui-legacy/TextField'
import Snackbar from 'material-ui-legacy/Snackbar'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

import { formatDate, dateToUTC } from '../../lib/formats'
import RelationalTextInput from '../UI/RelationalTextInput'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import AutoCompleteInput from '../UI/AutoCompleteInput'
import SaveButton from '../UI/SaveButton'
import CancelButton from '../UI/CancelButton'
import ErrorDialog from '../UI/ErrorDialog'
import NewClient from '../Clients/NewClient'
import NewAgency from '../Agencies/NewAgency'
import NewDirector from '../Directors/NewDirector'
import NewAgencyProducer from '../AgencyProducers/NewAgencyProducer'
import QuotesPager from '../Quotes/QuotesPager'
import UserSelector from '../Users/UserSelector'
import ProjectTypeSelector from '../ProjectTypes/ProjectTypeSelector'

import auth from '../../stores/auth.js'
import Resource from '../../stores/resource'
const ClientStore = Resource('clients')
const AgencyStore = Resource('agencies')
const AgencyProducerStore = Resource('agency-producers')
const DirectorStore = Resource('directors')
const ProjectStore = Resource('projects')

// INTERNAL COMPONENTS ---------------------------------------------------------

/*
Props:
- submitCallback: function(), called to submit form
- cancelCallback: function(), called to cancel submission
- saveLabel: String, label to show inside Save button (default: 'Save')
- formChanged,
  formValid,
  formSubmitted: Boolean, self-explanatory
*/
const FormActions = props => (
  <CardActions style={{ textAlign: 'center' }}>
    {props.submitCallback && (
      <SaveButton
        type="submit"
        label={props.saveLabel || 'Save'}
        disabled={!props.formChanged || !props.formValid}
        onTouchTap={props.submitCallback}
      />
    )}
    {props.cancelCallback && (
      <CancelButton
        onTouchTap={props.cancelCallback}
        disabled={!props.formChanged && props.formSubmitted}
      />
    )}
  </CardActions>
)

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- project: a complete project object INCLUDING:
           projectType, agency, agencyProducer, client and user
  or
- Project fields: will be inserted in the form when present
- action: 'new'|'edit' - some fields will be disabled depending on the situation
- submitCallback: function(project.attributes)
  called on form submission.
  This is *only* called when the form is valid and the contents have changed.
  Receives project attributes.
- cancelCallback: function()
  callback function for the cancel button.
- changedCallback: function(Boolean: changed)
  callback function called when the form contents have changed.
  Receives true if the contents have changed.
- forceChanged: Boolean: force the form status to "changed" (default: false)
*/
class ProjectForm extends React.PureComponent {
  constructor(props) {
    super(props)
    // We have to build the state on a regular variable so
    // React doesn't complain about direct state mutation.
    let initialState = {
      formValid: false,
      formChanged: this.props.forceChanged || false,
      formSubmitted: false,
      showQuoteForm: false,
      selectedQuote: null,
      error: '',
      snackbarMessage: ''
    }

    // We can either receive a single data prop or each field separately
    // To make our lives easier, we unpack included data here.
    if (this.props.project) {
      this.initialData = this.props.project.attributes

      // Related fields
      this.initialData.agencyName = this.initialData.agencyId
        ? this.props.project.included('agency').attributes.name
        : ''

      this.initialData.agencyProducerName = this.initialData.agencyProducerId
        ? this.props.project.included('agencyProducer').attributes.fullName
        : ''

      this.initialData.directorOneName = this.initialData.directorOneId
        ? this.props.project.included('directorOne').attributes.fullName
        : ''

      this.initialData.directorTwoName = this.initialData.directorTwoId
        ? this.props.project.included('directorTwo').attributes.fullName
        : ''

      this.initialData.clientName = this.initialData.clientId
        ? this.props.project.included('client').attributes.name
        : ''
    }
    else
    {
      this.initialData = clone(this.props)

      // Automatically assign project to current user when not specified
      this.initialData.userId = this.initialData.userId || auth.user().id
    }

    this.fields = [
      'projectTypeId',
      'docketNo',
      'name',
      'date',
      'contacts',
      'tags',
      'budget',
      'estimatedCost',
      'targetMargin',
      'product',
      'clientName',
      'clientId',
      'agencyProducerName',
      'agencyProducerId',
      'userId',
      'directorOneName',
      'directorOneId',
      'directorTwoName',
      'directorTwoId',
      'agencyName',
      'agencyId',
      'description',
      'jobDescription'
    ]
    for (let field of this.fields) {
      initialState[field] = {
        value: this.initialData[field] || '',
        valid: true
      }
    }

    // Adjust projectType - should be invalid when blank
    if (!initialState.projectTypeId.value) {
      initialState.projectTypeId.valid = false
    }

    // Adjust targetMargin
    initialState.targetMargin.value = initialState.targetMargin.value
      ? Number(initialState.targetMargin.value) * 100
      : null

    this.state = initialState // Et voilà!

    // Events to close relational forms
    this.closeAddAgencyEvent = new Event('closeAddAgencyEvent', {
      bubbles: true
    })
    this.closeAddAgencyProducerEvent = new Event(
      'closeAddAgencyProducerEvent',
      { bubbles: true }
    )
    this.closeAddDirectorOneEvent = new Event(
        'closeAddDirectorOneEvent',
      { bubbles: true }
    )
    this.closeAddDirectorTwoEvent = new Event(
        'closeAddDirectorTwoEvent',
        { bubbles: true }
    )

    this.closeAddClientEvent = new Event('closeAddClientEvent', {
      bubbles: true
    })
    this.closeQuoteFormEvent = new Event('closeQuoteFormEvent', {
      bubbles: true
    })

    // Debouncing change and validation hooks for better performance
    this._checkForChanges = debounce(this._checkForChanges, 250)
    this._validateForm = debounce(this._validateForm, 250)
  }

  _submit = () => {
    // Don't do anything if we have invalid fields or no changes.
    if (!this.state.formValid || !this.state.formChanged) return

    let attributes = {}
    for (let field of this.fields) {
      attributes[field] = this.state[field].value
    }
    if (this.props.submitCallback) this.props.submitCallback(attributes)
    this.setState({ formChanged: false, formSubmitted: true })
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = { value: value, valid: valid }
    this.setState(updates, () => {
      this._checkForChanges()
      this._validateForm()
      // this.forceUpdate()
    })
  }

  _setRelationalName = (textField, idField, value) => {
    let change = {}
    change[textField] = { value: value, valid: false }
    change[idField] = { value: '', valid: false }
    this.setState(change, () => {
      this._checkForChanges()
      this._validateForm()
    })
  }

  _checkForChanges = () => {
    let changed = this.props.forceChanged || false
    for (let field of this.fields) {
      let current = this.state[field].value
      let original = this.initialData[field] || ''
      if (current !== original) {
        changed = true
        break
      }
    }
    this.setState({ formChanged: changed }, () => {
      if (this.props.changedCallback) {
        this.props.changedCallback(this.state.formChanged)
      }
    })
  }

  _validateForm = () => {
    let allValid = true
    const skip = ['clientName', 'agencyName', 'agencyProducerName', 'directorOneName', 'directorTwoName', 'projectId']
    for (let field of this.fields) {
      if (!includes(skip, field) && !this.state[field].valid) {
        allValid = false
        break
      }
    }
    this.setState({ formValid: allValid })
  }

  _agencyCreated = agency => {
    this.setState(
      {
        agencyName: { value: agency.attributes.name, valid: true },
        agencyId: { value: agency.id, valid: true },
        clearAddAgency: true
      },
      () => {
        this._validateForm()
        setTimeout(() => document.dispatchEvent(this.closeAddAgencyEvent), 500)
      }
    )
  }

  _agencyProducerCreated = agencyProducer => {
    this.setState(
      {
        agencyProducerName: {
          value: agencyProducer.attributes.fullName,
          valid: true
        },
        agencyProducerId: { value: agencyProducer.id, valid: true },
        clearAddAgencyProducer: true
      },
      () => {
        this._validateForm()
        setTimeout(
          () => document.dispatchEvent(this.closeAddAgencyProducerEvent),
          500
        )
      }
    )
  }

  _directorOneCreated = director =>
  {
    this.setState(
      {
          directorOneName: { value: director.attributes.fullName, valid: true },
          directorOneId: { value: director.id, valid: true },
          clearAddDirector: true
      },
      () => {
        this._validateForm()
        setTimeout(
          () => document.dispatchEvent(this.closeAddDirectorOneEvent),
          500
        )
      }
    )
  }

  _directorTwoCreated = director =>
  {
      this.setState(
          {
              directorTwoName: { value: director.attributes.fullName, valid: true },
              directorTwoId: { value: director.id, valid: true },
              clearAddDirector: true
          },
          () => {
              this._validateForm()
              setTimeout(
                  () => document.dispatchEvent(this.closeAddDirectorTwoEvent),
                  500
              )
          }
      )
  }

  _clientCreated = client => {
    this.setState(
      {
        clientName: { value: client.attributes.name, valid: true },
        clientId: { value: client.id, valid: true },
        cost: { value: client.value, valid: true },
        clearAddClient: true
      },
      () => {
        this._validateForm()
        setTimeout(() => document.dispatchEvent(this.closeAddClientEvent), 500)
      }
    )
  }

  _resetSnackbar = () => this.setState({ snackbarMessage: '' })

  render() {
    const { redirect } = this.state;
    const project = this.props.project;

    if (redirect) return <Redirect push to={redirect} />

    const dateValue = this.state.date.value
      ? dateToUTC(this.state.date.value)
      : null

    const projectId = project ? project.id : this.state.projectId;

    const docketNumber = project
      ? `#${project.attributes.formattedDocketNo}`
      : "";

    const paperStyle = {
      padding: '10px'
    }

    const { action } = this.props
    const hasProject = Boolean(projectId)

    return (
      <section>
        <Row>
          <Col md="12" lg="12">
            <Card style={{ marginBottom: '40px' }}>
              <Row>
                <Col md="10">
                  <CardTitle
                    title={
                      this.props.formTitle ||
                      this.state.name.value ||
                      'Basic info'
                    }
                  />
                </Col>
                <Col md="2" style={{ textAlign: 'right' }}>
                  {hasProject && <CardTitle title={ docketNumber  } />}
                </Col>
              </Row>

                <CardText>
                  <Row style={{ marginBottom: '15px' }}>
                    <Col sm="12">
                      <Paper style={paperStyle}>
                        <ProjectTypeSelector
                          name="projectTypeId"
                          disabled={action === 'edit'}
                          fullWidth
                          required
                          unselectedLabel="Project type (required)"
                          hintText="Project type (required)"
                          errorMessage="You must provide a project type"
                          value={this.state.projectTypeId.value}
                          selectedIdCallback={projectTypeId =>
                            this._change(projectTypeId, true, 'projectTypeId')
                          }
                        />
                        <ValidatedTextInput
                            name="docketNo"
                            fullWidth
                            floatingLabelText="Docket Number"
                            hintText="Docket Number"
                            pattern={/^(\d{5})?$/}
                            errorMessage="You must provide a 5-digit number or leave this field blank to use legacy Docket Numbers"
                            value={this.state.docketNo.value}
                            validatedOnChange={this._change}
                        />
                      </Paper>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg="6" md="6" sm="12">
                      <Paper style={paperStyle}>
                        <ValidatedTextInput
                          name="name"
                          fullWidth
                          required
                          floatingLabelText="Campaign name (required)"
                          hintText="Campaign name (required)"
                          errorMessage="You must provide a campaign name"
                          value={this.state.name.value}
                          validatedOnChange={this._change}
                        />

                        <AutoCompleteInput
                          fullWidth
                          required
                          name="product"
                          dataStore={ProjectStore}
                          hintText="Product name (required)"
                          floatingLabelText="Product name (required)"
                          errorMessage="You must provide a product name"
                          value={this.state.product.value}
                          autoCompleteFilter="productAutocomplete"
                          autoCompleteAttribute="product"
                          validatedOnChange={this._change}
                        />

                        <RelationalTextInput
                          name="clientId"
                          required
                          fullWidth
                          openOnFocus
                          hintText="Client (must match, required)"
                          floatingLabelText="Client (must match, required)"
                          value={this.state.clientName.value}
                          selectedId={this.state.clientId.value}
                          dataStore={ClientStore}
                          autoCompleteFilter="nameAutocomplete"
                          autoCompleteAttribute="name"
                          validatedOnChange={this._change}
                          onTextChange={value =>
                            this._setRelationalName(
                              'clientName',
                              'clientId',
                              value
                            )
                          }
                          formCloseEvent={this.closeAddClientEvent}
                          formComponent={
                            <NewClient
                              formTitle="Add a new client"
                              attributes={{ name: this.state.clientName.value }}
                              forceChanged={true}
                              savedCallback={this._clientCreated}
                            />
                          }
                        />

                        <RelationalTextInput
                          name="agencyId"
                          required
                          fullWidth
                          openOnFocus
                          hintText="Agency (must match, required)"
                          floatingLabelText="Agency (must match, required)"
                          value={this.state.agencyName.value}
                          selectedId={this.state.agencyId.value}
                          dataStore={AgencyStore}
                          autoCompleteFilter="nameAutocomplete"
                          autoCompleteAttribute="name"
                          validatedOnChange={this._change}
                          onTextChange={value =>
                            this._setRelationalName(
                              'agencyName',
                              'agencyId',
                              value
                            )
                          }
                          formCloseEvent={this.closeAddAgencyEvent}
                          formComponent={
                            <NewAgency
                              formTitle="Add a new agency"
                              attributes={{ name: this.state.agencyName.value }}
                              forceChanged={true}
                              savedCallback={this._agencyCreated}
                            />
                          }
                        />

                        <RelationalTextInput
                          name="agencyProducerId"
                          fullWidth
                          openOnFocus
                          hintText="External project manager (must match)"
                          floatingLabelText="External project manager (must match)"
                          value={this.state.agencyProducerName.value}
                          selectedId={this.state.agencyProducerId.value}
                          dataStore={AgencyProducerStore}
                          autoCompleteFilter="nameAutocomplete"
                          additionalFilters={
                            this.state.agencyId.value
                              ? { agencyId: this.state.agencyId.value }
                              : null
                          }
                          autoCompleteAttribute="fullName"
                          validatedOnChange={this._change}
                          onTextChange={value =>
                            this._setRelationalName(
                              'agencyProducerName',
                              'agencyProducerId',
                              value
                            )
                          }
                          formCloseEvent={this.closeAddAgencyProducerEvent}
                          formComponent={
                            <NewAgencyProducer
                              formTitle="Add a new External project manager"
                              attributes={{
                                firstName: this.state.agencyProducerName.value.split(
                                  ' '
                                )[0],
                                lastName: this.state.agencyProducerName.value.split(
                                  ' '
                                )[1],
                                agencyId: this.state.agencyId.value,
                                agencyName: this.state.agencyName.value
                              }}
                              forceChanged={true}
                              savedCallback={this._agencyProducerCreated}
                            />
                          }
                        />
                        <RelationalTextInput
                          name="directorOneId"
                          required
                          fullWidth
                          openOnFocus
                          hintText="Primary Director (required)"
                          floatingLabelText="Primary Director (required)"
                          value={this.state.directorOneName.value}
                          selectedId={this.state.directorOneId.value}
                          dataStore={DirectorStore}
                          autoCompleteFilter="nameAutocomplete"
                          autoCompleteAttribute="fullName"
                          validatedOnChange={this._change}
                          onTextChange={value =>
                            this._setRelationalName(
                              'directorOneName',
                              'directorOneId',
                              value
                            )
                          }
                          formCloseEvent={this.closeAddDirectorOneEvent}
                          formComponent={
                            <NewDirector
                              formTitle="Add a new Director"
                              attributes={{
                                firstName: this.state.directorOneName.value.split(
                                  ' '
                                )[0],
                                lastName: this.state.directorOneName.value.split(
                                  ' '
                                )[1],
                              }}
                              forceChanged={true}
                              savedCallback={this._directorOneCreated}
                            />
                          }
                        />
                      </Paper>
                    </Col>

                    <Col md="6" lg="6" sm="12">
                      <Paper style={paperStyle}>
                        <DatePicker
                          fullWidth
                          hintText="Project start date"
                          floatingLabelText="Project start date"
                          value={dateValue}
                          defaultDate={dateToUTC()}
                          formatDate={date => formatDate(date)}
                          onChange={(ev, value) =>
                            this._change(value, true, 'date')
                          }
                        />

                        <ValidatedTextInput
                          name="budget"
                          fullWidth
                          floatingLabelText="Estimated budget ($)"
                          hintText="12345.67"
                          pattern={/^(\d+)\.?(\d+)?$/}
                          errorMessage="Invalid value"
                          value={this.state.budget.value}
                          validatedOnChange={this._change}
                        />

                        <ValidatedTextInput
                          name="estimatedCost"
                          fullWidth
                          floatingLabelText="Estimated cost (ballpark $)"
                          hintText="12345.67"
                          pattern={/^(\d+)\.?(\d+)?$/}
                          errorMessage="Invalid value"
                          value={this.state.estimatedCost.value}
                          validatedOnChange={this._change}
                        />

                        <ValidatedTextInput
                          name="targetMargin"
                          fullWidth
                          floatingLabelText="Target margin (%)"
                          hintText="50"
                          pattern={/^(\d+)\.?(\d+)?$/}
                          errorMessage="Invalid value"
                          value={this.state.targetMargin.value}
                          validatedOnChange={this._change}
                        />

                        <UserSelector
                          name="userId"
                          unselectedLabel="Select an internal producer"
                          floatingLabelText="Internal producer"
                          value={this.state.userId.value}
                          filter={{ role: 'producer' }}
                          fullWidth
                          onSelected={(userId, user) =>
                            this._change(userId, true, 'userId')
                          }
                        />
                          <RelationalTextInput
                              name="directorTwoId"
                              fullWidth
                              openOnFocus
                              hintText="Secondary Director"
                              floatingLabelText="Secondary Director"
                              value={this.state.directorTwoName.value}
                              selectedId={this.state.directorTwoId.value}
                              dataStore={DirectorStore}
                              autoCompleteFilter="nameAutocomplete"
                              autoCompleteAttribute="fullName"
                              validatedOnChange={this._change}
                              onTextChange={value =>
                                  this._setRelationalName(
                                      'directorTwoName',
                                      'directorTwoId',
                                      value
                                  )
                              }
                              formCloseEvent={this.closeAddDirectorTwoEvent}
                              formComponent={
                                  <NewDirector
                                      formTitle="Add a new Director"
                                      attributes={{
                                          firstName: this.state.directorTwoName.value.split(
                                              ' '
                                          )[0],
                                          lastName: this.state.directorTwoName.value.split(
                                              ' '
                                          )[1],
                                      }}
                                      forceChanged={true}
                                      savedCallback={this._directorTwoCreated}
                                  />
                              }
                          />
                      </Paper>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="12" style={{ marginTop: '30px' }}>
                      <Paper style={paperStyle}>
                        <TextField
                          name="jobDescription"
                          floatingLabelText="Job description (visible to client)"
                          multiLine
                          fullWidth
                          rows={3}
                          rowsMax={15}
                          value={this.state.jobDescription.value}
                          onChange={(ev, value) =>
                            this._change(value, true, 'jobDescription')
                          }
                        />
                        <TextField
                          name="description"
                          floatingLabelText="Requisition (internal, paste requisition email here)"
                          multiLine
                          fullWidth
                          rows={3}
                          rowsMax={15}
                          value={this.state.description.value}
                          onChange={(ev, value) =>
                            this._change(value, true, 'description')
                          }
                        />
                      </Paper>
                    </Col>
                  </Row>
                </CardText>

              <FormActions
                submitCallback={this._submit}
                cancelCallback={this.props.cancelCallback}
                formChanged={this.state.formChanged}
                formValid={this.state.formValid}
                formSubmitted={this.state.formSubmitted}
                saveLabel={projectId ? 'Save' : 'Save and proceed to quoting'}
              />
            </Card>
          </Col>

          {projectId && <QuotesPager projectId={projectId} />}
        </Row>

        <Snackbar
          open={Boolean(this.state.snackbarMessage)}
          message={this.state.snackbarMessage}
          autoHideDuration={4000}
          onRequestClose={this._resetSnackbar}
        />

        <ErrorDialog
          open={Boolean(this.state.error)}
          closeCallback={() => this.setState({ error: '' })}
        >
          <p>
            <strong>Error:</strong> {this.state.error}
          </p>
        </ErrorDialog>
      </section>
    )
  }
}

export default ProjectForm



// WEBPACK FOOTER //
// ./src/components/Projects/ProjectForm.jsx